import React, { useState, useEffect, useCallback } from 'react';
import { Link, useParams, useLocation } from 'react-router-dom';
import { bookingAPI } from '../services/api';

const BookingSuccess = () => {
    const { bookingId } = useParams();
    const location = useLocation();
    const [booking, setBooking] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchBookingDetails = useCallback(async () => {
        try {
            setLoading(true);
            
            // Try to get from API
            const response = await bookingAPI.trackBooking(bookingId);
            console.log('Booking API Response:', response.data);
            
            if (response.data && response.data.data) {
                setBooking(response.data.data);
            } else if (response.data) {
                setBooking(response.data);
            } else if (location.state) {
                // Fallback to passed state
                setBooking(location.state);
            } else {
                throw new Error('No booking data found');
            }
        } catch (err) {
            console.error('Error fetching booking:', err);
            setError('Failed to load booking details');
            
            // Try to use location state as fallback
            if (location.state) {
                setBooking(location.state);
                setError(null);
            }
        } finally {
            setLoading(false);
        }
    }, [bookingId, location.state]);

    useEffect(() => {
        fetchBookingDetails();
    }, [fetchBookingDetails]);

    const formatDate = (dateString) => {
        if (!dateString) return 'Not specified';
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return 'Invalid Date';
            return date.toLocaleDateString('en-IN', {
                day: 'numeric',
                month: 'long',
                year: 'numeric'
            });
        } catch {
            return dateString;
        }
    };

    const formatCurrency = (amount) => {
        if (!amount) return '0';
        return parseFloat(amount).toLocaleString('en-IN');
    };

    if (loading) {
        return (
            <div className="booking-success-loading">
                <div className="spinner"></div>
                <p>Loading booking details...</p>
                <style>{`
                    .booking-success-loading {
                        min-height: 100vh;
                        display: flex;
                        flex-direction: column;
                        align-items: center;
                        justify-content: center;
                        background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
                    }
                    .spinner {
                        width: 50px;
                        height: 50px;
                        border: 4px solid #e2e8f0;
                        border-top-color: #ec3134;
                        border-radius: 50%;
                        animation: spin 1s linear infinite;
                        margin-bottom: 1rem;
                    }
                    @keyframes spin {
                        to { transform: rotate(360deg); }
                    }
                `}</style>
            </div>
        );
    }

    if (error || !booking) {
        return (
            <div className="booking-success-error">
                <div className="error-icon">⚠️</div>
                <h2>Booking Not Found</h2>
                <p>{error || 'The booking you are looking for does not exist.'}</p>
                <Link to="/" className="home-btn">Go to Home</Link>
                <style>{`
                    .booking-success-error {
                        min-height: 100vh;
                        display: flex;
                        flex-direction: column;
                        align-items: center;
                        justify-content: center;
                        text-align: center;
                        padding: 2rem;
                        background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
                    }
                    .error-icon {
                        font-size: 4rem;
                        margin-bottom: 1rem;
                    }
                    h2 {
                        color: #1e293b;
                        margin-bottom: 1rem;
                    }
                    p {
                        color: #64748b;
                        margin-bottom: 2rem;
                    }
                    .home-btn {
                        padding: 0.75rem 2rem;
                        background: #ec3134;
                        color: white;
                        text-decoration: none;
                        border-radius: 50px;
                        font-weight: 600;
                        transition: all 0.3s;
                    }
                    .home-btn:hover {
                        transform: translateY(-2px);
                        box-shadow: 0 10px 25px rgba(236, 49, 52, 0.3);
                    }
                `}</style>
            </div>
        );
    }

    return (
        <div className="booking-success-page">
            <div className="success-container">
                <div className="success-header">
                    <div className="success-icon">
                        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                            <polyline points="22 4 12 14.01 9 11.01"></polyline>
                        </svg>
                    </div>
                    <h1>Booking Confirmed! 🎉</h1>
                    <p>Your booking has been successfully created.</p>
                </div>

                <div className="booking-details-card">
                    <h2>Booking Details</h2>
                    
                    <div className="detail-row">
                        <span className="detail-label">Booking ID</span>
                        <span className="detail-value booking-id">{booking.booking_id || booking.bookingId || bookingId}</span>
                    </div>

                    <div className="detail-row">
                        <span className="detail-label">Package</span>
                        <span className="detail-value">{booking.package_title || booking.packageTitle || 'Package Name'}</span>
                    </div>

                    <div className="detail-row">
                        <span className="detail-label">Customer Name</span>
                        <span className="detail-value">{booking.customer_name || booking.customerName}</span>
                    </div>

                    <div className="detail-row">
                        <span className="detail-label">Email</span>
                        <span className="detail-value">{booking.customer_email || booking.customerEmail}</span>
                    </div>

                    <div className="detail-row">
                        <span className="detail-label">Phone</span>
                        <span className="detail-value">{booking.customer_phone || booking.customerPhone}</span>
                    </div>

                    <div className="detail-row">
                        <span className="detail-label">Travel Date</span>
                        <span className="detail-value">{formatDate(booking.travel_date || booking.travelDate)}</span>
                    </div>

                    <div className="detail-row">
                        <span className="detail-label">Travelers</span>
                        <span className="detail-value">
                            {booking.adults || 0} Adult{booking.adults !== 1 ? 's' : ''}
                            {booking.children > 0 ? `, ${booking.children} Child${booking.children !== 1 ? 'ren' : ''}` : ''}
                        </span>
                    </div>

                    <div className="detail-row total">
                        <span className="detail-label">Total Amount</span>
                        <span className="detail-value">₹{formatCurrency(booking.total_amount || booking.totalAmount)}</span>
                    </div>

                    {Number(booking.addons_amount || 0) > 0 && (
                        <div className="detail-row">
                            <span className="detail-label">Add-on Services</span>
                            <span className="detail-value">₹{formatCurrency(booking.addons_amount)}</span>
                        </div>
                    )}

                    {/* <div className="detail-row">
                        <span className="detail-label">Payment Status</span>
                        <span className={`detail-value status-badge ${booking.payment_status || booking.paymentStatus || 'pending'}`}>
                            {booking.payment_status || booking.paymentStatus || 'Pending'}
                        </span>
                    </div> */}

                    <div className="detail-row">
                        <span className="detail-label">Booking Status</span>
                        <span className="confirmed">
                            Confirmed
                        </span>
                    </div>
                </div>

                {Array.isArray(booking.addon_services) && booking.addon_services.length > 0 && (
                    <div className="special-requests-card">
                        <h3>Selected Add-on Services</h3>
                        {booking.addon_services.map((addon, index) => (
                            <p key={`${addon.addon_service_id || index}-${index}`}>
                                {addon.title_snapshot || addon.title} x{Number(addon.quantity || 1)} - ₹{formatCurrency(addon.total_price || 0)}
                            </p>
                        ))}
                    </div>
                )}

                {booking.special_requests && (
                    <div className="special-requests-card">
                        <h3>Special Requests</h3>
                        <p>{booking.special_requests}</p>
                    </div>
                )}

                <div className="next-steps">
                    <h3>Next Steps</h3>
                    <ul>
                        <li>
                            <i className="fas fa-envelope"></i>
                            <span>You will receive a confirmation email shortly</span>
                        </li>
                        <li>
                            <i className="fas fa-phone-alt"></i>
                            <span>Our team will contact you within 24 hours</span>
                        </li>
                        <li>
                            <i className="fas fa-calendar-check"></i>
                            <span>Save your booking ID for future reference</span>
                        </li>
                    </ul>
                </div>

                <div className="action-buttons">
                    <Link
                        to={`/invoice/${booking.booking_id || booking.bookingId || bookingId}?download=1`}
                        state={{ booking }}
                        className="btn-packages"
                    >
                        <i className="fas fa-file-invoice"></i>
                        Download Invoice
                    </Link>
                    <Link to="/" className="btn-home">
                        <i className="fas fa-home"></i>
                        Go to Home
                    </Link>
                    <Link to="/packages" className="btn-packages">
                        <i className="fas fa-box"></i>
                        Browse More Packages
                    </Link>
                </div>

                <div className="contact-support">
                    <p>Need help? Contact our support team</p>
                    <a href="tel:+919335444047" className="support-phone">
                        <i className="fas fa-phone-alt"></i> +91 9335444047
                    </a>
                    <a href="mailto:bookings@redgotrips.com" className="support-email">
                        <i className="fas fa-envelope"></i> bookings@redgotrips.com
                    </a>
                </div>
            </div>

            <style>{`
                .booking-success-page {
                    min-height: 100vh;
                    background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
                    padding: 3rem 1rem;
                }

                .success-container {
                    max-width: 800px;
                    margin: 0 auto;
                    background: white;
                    border-radius: 32px;
                    padding: 3rem;
                    box-shadow: 0 20px 40px rgba(0,0,0,0.1);
                    animation: slideUp 0.5s ease;
                }

                @keyframes slideUp {
                    from {
                        opacity: 0;
                        transform: translateY(30px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }

                .success-header {
                    text-align: center;
                    margin-bottom: 2.5rem;
                }

                .success-icon {
                    width: 80px;
                    height: 80px;
                    background: linear-gradient(135deg, #22c55e, #16a34a);
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    margin: 0 auto 1.5rem;
                    color: white;
                    animation: scaleIn 0.5s ease;
                }

                @keyframes scaleIn {
                    from { transform: scale(0); }
                    to { transform: scale(1); }
                }

                .success-header h1 {
                    font-size: 2rem;
                    color: #1e293b;
                    margin-bottom: 0.5rem;
                }

                .success-header p {
                    color: #64748b;
                }

                .booking-details-card {
                    background: #f8fafc;
                    border-radius: 20px;
                    padding: 1.5rem;
                    margin-bottom: 1.5rem;
                }

                .booking-details-card h2 {
                    font-size: 1.2rem;
                    color: #1e293b;
                    margin-bottom: 1rem;
                    padding-bottom: 0.5rem;
                    border-bottom: 2px solid #e2e8f0;
                }

                .detail-row {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 0.75rem 0;
                    border-bottom: 1px solid #e2e8f0;
                }

                .detail-row:last-child {
                    border-bottom: none;
                }

                .detail-row.total {
                    font-weight: 700;
                    font-size: 1.1rem;
                    color: #ec3134;
                }

                .detail-label {
                    color: #64748b;
                    font-size: 0.9rem;
                }

                .detail-value {
                    color: #1e293b;
                    font-weight: 500;
                    font-size: 0.9rem;
                }

                .detail-value.booking-id {
                    font-family: monospace;
                    font-size: 0.8rem;
                    background: #e2e8f0;
                    padding: 0.25rem 0.75rem;
                    border-radius: 20px;
                }

                .status-badge {
                    padding: 0.25rem 0.75rem;
                    border-radius: 20px;
                    font-size: 0.75rem;
                    font-weight: 600;
                    text-transform: capitalize;
                }

                .status-badge.completed, .status-badge.confirmed {
                    background: #dcfce7;
                    color: #166534;
                }

                .status-badge.pending {
                    background: #fed7aa;
                    color: #9a3412;
                }

                .status-badge.cancelled {
                    background: #fee2e2;
                    color: #991b1b;
                }

                .special-requests-card {
                    background: #fff7ed;
                    border-radius: 20px;
                    padding: 1.5rem;
                    margin-bottom: 1.5rem;
                }

                .special-requests-card h3 {
                    font-size: 1rem;
                    color: #9a3412;
                    margin-bottom: 0.5rem;
                }

                .special-requests-card p {
                    color: #7b341e;
                    line-height: 1.6;
                }

                .next-steps {
                    margin-bottom: 1.5rem;
                }

                .next-steps h3 {
                    font-size: 1rem;
                    color: #1e293b;
                    margin-bottom: 0.75rem;
                }

                .next-steps ul {
                    list-style: none;
                    padding: 0;
                }

                .next-steps li {
                    display: flex;
                    align-items: center;
                    gap: 0.75rem;
                    padding: 0.75rem;
                    background: #f1f5f9;
                    border-radius: 12px;
                    margin-bottom: 0.5rem;
                }

                .next-steps li i {
                    width: 20px;
                    color: #ec3134;
                }

                .action-buttons {
                    display: flex;
                    gap: 1rem;
                    margin-bottom: 1.5rem;
                }

                .btn-home, .btn-packages {
                    flex: 1;
                    padding: 0.75rem;
                    border-radius: 50px;
                    font-weight: 600;
                    text-decoration: none;
                    text-align: center;
                    transition: all 0.3s;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 0.5rem;
                }

                .btn-home {
                    background: #f1f5f9;
                    color: #1e293b;
                }

                .btn-home:hover {
                    background: #e2e8f0;
                    transform: translateY(-2px);
                }

                .btn-packages {
                    background: linear-gradient(135deg, #ec3134, #c2410c);
                    color: white;
                }

                .btn-packages:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 10px 25px rgba(236, 49, 52, 0.3);
                }

                .contact-support {
                    text-align: center;
                    padding-top: 1.5rem;
                    border-top: 1px solid #e2e8f0;
                }

                .contact-support p {
                    color: #64748b;
                    font-size: 0.85rem;
                    margin-bottom: 0.5rem;
                }

                .support-phone, .support-email {
                    display: inline-block;
                    margin: 0 0.5rem;
                    color: #1e293b;
                    text-decoration: none;
                    font-size: 0.85rem;
                }

                .support-phone i, .support-email i {
                    margin-right: 0.25rem;
                    color: #ec3134;
                }

                @media (max-width: 640px) {
                    .success-container {
                        padding: 1.5rem;
                    }

                    .action-buttons {
                        flex-direction: column;
                    }

                    .detail-row {
                        flex-direction: column;
                        align-items: flex-start;
                        gap: 0.25rem;
                    }

                    .detail-value {
                        width: 100%;
                    }
                }
            `}</style>
        </div>
    );
};

export default BookingSuccess;
